import React, { useEffect, useMemo, useState } from "react";
import { Badge, Card, Col, Form, ListGroup, Row, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";

const GRADE_THRESHOLDS = [
  { min: 80, grade: 4 },
  { min: 75, grade: 3.5 },
  { min: 70, grade: 3 },
  { min: 65, grade: 2.5 },
  { min: 60, grade: 2 },
  { min: 55, grade: 1.5 },
  { min: 50, grade: 1 },
  { min: 0, grade: 0 },
];

const getValue = (row, keys, fallback = "-") => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== "") return row[key];
  }
  return fallback;
};

const calcGrade = (score) => {
  if (score === null || score === undefined || score === "") return null;
  const numericScore = parseFloat(score);
  if (Number.isNaN(numericScore)) return null;
  const threshold = GRADE_THRESHOLDS.find((item) => numericScore >= item.min);
  return threshold ? threshold.grade : 0;
};

const getStudentFullName = (student) => (
  getValue(student, ["st_name", "full_name", "fullname", "name"], "")
);

const getStudentIdNumber = (student) => (
  getValue(student, ["st_idnumber", "idnumber", "student_id", "studentId"], "")
);

const formatStatusBadge = (status) => {
  if (String(status) === "0") return <Badge bg="danger">พ้นสภาพ</Badge>;
  if (String(status) === "1") return <Badge bg="primary">กำลังศึกษา</Badge>;
  if (status !== undefined && status !== null && status !== "") return <Badge bg="success">นักเรียนใหม่</Badge>;
  return <Badge bg="secondary">ไม่ระบุ</Badge>;
};

const GradeBadge = ({ row }) => {
  const hasGradeRecord = getValue(row, ["g_id", "grade_id"], null) !== null;
  const score = getValue(row, ["g_grade", "grade_score", "score"], null);
  const waiting = String(getValue(row, ["g_waiting", "waiting"], "0")) === "1";

  if (!hasGradeRecord && (score === null || score === "-")) return <span className="text-muted">-</span>;
  if (waiting) return <span className="text-danger fw-bold">ร</span>;

  const grade = calcGrade(score);
  if (grade === null) return <span className="text-muted">-</span>;

  return (
    <Badge bg={grade >= 2 ? "success" : grade >= 1 ? "warning" : "danger"}>
      {grade}
    </Badge>
  );
};

// การ์ดรายวิชา 1 รายการ สำหรับมุมมองมือถือ
const SubjectGradeCard = ({ row }) => {
  const subjectId = getValue(row, ["sub_subid", "subject_code", "sub_code"], "-");
  const subjectName = getValue(row, ["sub_subname", "subject_name", "sub_name"], "-");
  const subjectType = getValue(row, ["sub_type", "subject_type"], "");
  const score = getValue(row, ["g_grade", "grade_score", "score"], "-");
  return (
    <div className="border rounded p-3">
      <div className="fw-semibold">
        <span className="text-muted">{subjectId}</span> {subjectName}
        {subjectType === "extra" && <span className="text-muted ms-1 fs-sm">(เพิ่มเติม)</span>}
      </div>
      <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">ครูผู้สอน:</span> {getValue(row, ["teacher_name", "t_name"], "-")}</div>
      </div>
      <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top">
        <div><span className="text-muted">คะแนน:</span> <span className="text-primary">{score}</span></div>
        <GradeBadge row={row} />
      </div>
    </div>
  );
};

const SemesterTable = ({ rows, semester, view }) => {
  const semesterRows = rows.filter((row) => String(getValue(row, ["sub_semester", "semester", "term"], "")) === String(semester));

  if (view === 'card') {
    return (
      <div>
        <div className="text-center fw-semibold mb-2">ภาคเรียนที่ {semester}</div>
        {semesterRows.length > 0 ? (
          <div className="d-flex flex-column gap-2">
            {semesterRows.map((row, index) => (
              <SubjectGradeCard key={`${getValue(row, ["teach_id", "teaching_id"], "teach")}-${getValue(row, ["sub_id", "subject_id"], "sub")}-${index}`} row={row} />
            ))}
          </div>
        ) : (
          <div className="text-center text-muted py-3">ไม่พบข้อมูล</div>
        )}
      </div>
    );
  }

  return (
    <Table responsive bordered hover size="sm" className="mb-0 align-middle">
      <thead>
        <tr className="text-center">
          <th colSpan={5}>ภาคเรียนที่ {semester}</th>
        </tr>
        <tr>
          <th className="text-center" style={{ width: 90 }}>รหัสวิชา</th>
          <th>ชื่อวิชา</th>
          <th className="text-center" style={{ width: 140 }}>ครูผู้สอน</th>
          <th className="text-center" style={{ width: 70 }}>คะแนน</th>
          <th className="text-center" style={{ width: 85 }}>ผลการเรียน</th>
        </tr>
      </thead>
      <tbody>
        {semesterRows.length > 0 ? semesterRows.map((row, index) => {
          const subjectId = getValue(row, ["sub_subid", "subject_code", "sub_code"], "-");
          const subjectName = getValue(row, ["sub_subname", "subject_name", "sub_name"], "-");
          const subjectType = getValue(row, ["sub_type", "subject_type"], "");
          const score = getValue(row, ["g_grade", "grade_score", "score"], "-");

          return (
            <tr key={`${getValue(row, ["teach_id", "teaching_id"], "teach")}-${getValue(row, ["sub_id", "subject_id"], "sub")}-${index}`}>
              <td className="text-center">{subjectId}</td>
              <td>
                {subjectName}
                {subjectType === "extra" && <span className="text-muted ms-1 fs-sm">(เพิ่มเติม)</span>}
              </td>
              <td className="text-center">{getValue(row, ["teacher_name", "t_name"], "-")}</td>
              <td className="text-center text-primary">{score}</td>
              <td className="text-center"><GradeBadge row={row} /></td>
            </tr>
          );
        }) : (
          <tr>
            <td colSpan={5} className="text-center text-muted py-3">ไม่พบข้อมูล</td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

export default function StudentGradeReview() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [, setSkin] = useState(currentSkin);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [gradeResult, setGradeResult] = useState(null);
  const [gradeLoading, setGradeLoading] = useState(false);
  const [gradeError, setGradeError] = useState("");
  const [view, setView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table');

  useEffect(() => {
    const trimmedQuery = query.trim();
    setSearchError("");

    if (selectedStudent) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    if (trimmedQuery.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await axiosInstance.get("/api/students/search", {
          params: { q: trimmedQuery },
          signal: controller.signal,
        });
        setSearchResults(Array.isArray(response.data?.data) ? response.data.data : []);
      } catch (error) {
        if (error.name !== "CanceledError" && error.code !== "ERR_CANCELED") {
          setSearchResults([]);
          setSearchError(error.response?.data?.message || "ไม่สามารถค้นหานักเรียนได้");
        }
      } finally {
        if (!controller.signal.aborted) setSearchLoading(false);
      }
    }, 350);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [query, selectedStudent]);

  const handleSelectStudent = async (student) => {
    const idNumber = getStudentIdNumber(student);
    if (!idNumber) return;

    setSelectedStudent(student);
    setQuery(`${idNumber} ${getStudentFullName(student)}`.trim());
    setSearchResults([]);
    setGradeResult(null);
    setGradeError("");
    setGradeLoading(true);

    try {
      const response = await axiosInstance.get(`/api/getStudentGradesByIdNumber/${encodeURIComponent(idNumber)}`);
      setGradeResult(response.data);
    } catch (error) {
      setGradeError(error.response?.data?.message || "ไม่สามารถโหลดผลการเรียนได้");
    } finally {
      setGradeLoading(false);
    }
  };

  const academicYearGroups = useMemo(() => {
    const rows = Array.isArray(gradeResult?.rows) ? gradeResult.rows : [];
    const groups = new Map();

    rows.forEach((row) => {
      const yearId = getValue(row, ["year_id", "academic_year_id"], "unknown");
      const yearName = getValue(row, ["year_year", "academic_year", "year"], "ไม่ระบุปีการศึกษา");
      const className = getValue(row, ["c_class", "class_name"], "");
      const roomNo = getValue(row, ["r_no", "room_no"], "");
      const key = String(yearId);

      if (!groups.has(key)) {
        groups.set(key, {
          yearId,
          yearName,
          className,
          roomNo,
          rows: [],
        });
      }
      groups.get(key).rows.push(row);
    });

    return Array.from(groups.values()).sort((a, b) => {
      const yearA = Number(a.yearName);
      const yearB = Number(b.yearName);
      if (!Number.isNaN(yearA) && !Number.isNaN(yearB)) return yearB - yearA;
      return String(b.yearName).localeCompare(String(a.yearName));
    });
  }, [gradeResult]);

  const displayStudent = gradeResult?.student || selectedStudent;

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">หัวหน้าวิชาการสามัญ</Link></li>
              <li className="breadcrumb-item active">ตรวจสอบผลการเรียนรายบุคคล</li>
            </ol>
            <h4 className="main-title mb-0">ตรวจสอบผลการเรียนรายบุคคล</h4>
          </div>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Form.Group className="position-relative">
              <Form.Label className="fw-semibold">ค้นหานักเรียน</Form.Label>
              <Form.Control
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setSelectedStudent(null);
                  setGradeResult(null);
                  setGradeError("");
                }}
                placeholder="กรอกเลขประจำตัวนักเรียน หรือชื่อ-สกุล"
                autoComplete="off"
              />
              <Form.Text className="text-muted">พิมพ์อย่างน้อย 2 ตัวอักษร ระบบจะแสดงรายชื่อนักเรียนแบบเรียลไทม์</Form.Text>

              {(searchLoading || searchError || searchResults.length > 0 || (query.trim().length >= 2 && !searchLoading && !selectedStudent)) && (
                <Card className="position-absolute w-100 shadow-sm mt-1" style={{ zIndex: 20 }}>
                  <ListGroup variant="flush">
                    {searchLoading && (
                      <ListGroup.Item className="text-muted">
                        <Spinner animation="border" size="sm" className="me-2" />กำลังค้นหา...
                      </ListGroup.Item>
                    )}
                    {!searchLoading && searchError && (
                      <ListGroup.Item className="text-danger">{searchError}</ListGroup.Item>
                    )}
                    {!searchLoading && !searchError && searchResults.map((student) => (
                      <ListGroup.Item
                        action
                        key={getValue(student, ["st_id", "id", "st_idnumber"], getStudentIdNumber(student))}
                        onClick={() => handleSelectStudent(student)}
                        className="d-flex justify-content-between align-items-start gap-3"
                      >
                        <div>
                          <div className="fw-semibold">{getStudentFullName(student)}</div>
                          <div className="text-muted fs-sm">เลขประจำตัว: {getStudentIdNumber(student)}</div>
                        </div>
                        {student.c_class && <Badge bg="light" text="dark">{student.c_class}</Badge>}
                      </ListGroup.Item>
                    ))}
                    {!searchLoading && !searchError && searchResults.length === 0 && query.trim().length >= 2 && !selectedStudent && (
                      <ListGroup.Item className="text-muted">ไม่พบนักเรียนที่ตรงกับคำค้นหา</ListGroup.Item>
                    )}
                  </ListGroup>
                </Card>
              )}
            </Form.Group>
          </Card.Body>
        </Card>

        {gradeLoading && (
          <div className="text-center text-muted py-4">
            <Spinner animation="border" size="sm" className="me-2" />กำลังโหลดผลการเรียน...
          </div>
        )}

        {!gradeLoading && gradeError && (
          <Card className="card-one mb-3">
            <Card.Body className="text-danger">
              <i className="ri-error-warning-line me-1"></i>{gradeError}
            </Card.Body>
          </Card>
        )}

        {!gradeLoading && gradeResult && (
          <React.Fragment>
            <Card className="card-one mb-3">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-4 flex-wrap">
                  <div>
                    <span className="text-muted fs-sm">เลขประจำตัว</span>
                    <div className="fw-semibold">{getStudentIdNumber(displayStudent)}</div>
                  </div>
                  <div>
                    <span className="text-muted fs-sm">ชื่อ-สกุล</span>
                    <div className="fw-semibold">{getStudentFullName(displayStudent)}</div>
                  </div>
                  <div>
                    <span className="text-muted fs-sm">สถานะ</span>
                    <div>{formatStatusBadge(displayStudent?.st_status)}</div>
                  </div>
                  <Badge bg="secondary" className="ms-sm-auto">อ่านอย่างเดียว</Badge>
                </div>
              </Card.Body>
            </Card>

            {academicYearGroups.length === 0 ? (
              <Card className="card-one">
                <Card.Body className="text-center text-muted py-4">ไม่พบข้อมูลผลการเรียน</Card.Body>
              </Card>
            ) : academicYearGroups.map((group, idx) => (
              <Card className="card-one mb-3" key={`${group.yearId}-${group.yearName}`}>
                <Card.Header className="d-flex flex-wrap align-items-center gap-2">
                  <Card.Title as="h6" className="mb-0">
                    <i className="ri-graduation-cap-line me-1"></i>
                    ปีการศึกษา {group.yearName}
                    {(group.className || group.roomNo) && (
                      <span className="text-muted ms-2 fw-normal">
                        {group.className}{group.roomNo ? `/${group.roomNo}` : ""}
                      </span>
                    )}
                  </Card.Title>
                  {idx === 0 && (
                    <div className="btn-group btn-group-sm ms-auto" role="group" aria-label="สลับมุมมอง">
                      <button type="button" className={`btn btn${view === 'table' ? '' : '-outline'}-primary`}
                        onClick={() => setView('table')} title="มุมมองตาราง">
                        <i className="ri-table-line" />
                      </button>
                      <button type="button" className={`btn btn${view === 'card' ? '' : '-outline'}-primary`}
                        onClick={() => setView('card')} title="มุมมองการ์ด">
                        <i className="ri-layout-grid-line" />
                      </button>
                    </div>
                  )}
                </Card.Header>
                <Card.Body className="p-2">
                  <Row className="g-3">
                    <Col lg="6">
                      <SemesterTable rows={group.rows} semester="1" view={view} />
                    </Col>
                    <Col lg="6">
                      <SemesterTable rows={group.rows} semester="2" view={view} />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
          </React.Fragment>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}
